<template>
    <div :class="['nw-chat-widget', themeClass]">
        <ChatDialog :api-url="apiUrl" :open="isOpen" @close="emit('close')" />
    </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import ChatDialog from './components/ChatDialog.vue';

defineOptions({
    name: 'NocwareChatWidget',
});

const props = withDefaults(
    defineProps<{
        // HTML attributes are always strings; accept both boolean and string "true"/"false"/"".
        open?: boolean | string;
        apiUrl?: string;
    }>(),
    {
        open: true,
        apiUrl: import.meta.env.VITE_API_URL ?? '/ai-gateway/api/v1',
    },
);

const emit = defineEmits<{ close: [] }>();

const darkMode = ref(readDarkMode(resolveThemeTarget()));
let themeObserver: MutationObserver | null = null;

// Normalise the open prop — HTML sets open="true" (string), Vue bindings set :open="true" (boolean).
const isOpen = computed(() => {
    const v = props.open;
    return v === true || v === '' || v === 'true' || v === 'open';
});

const themeClass = computed(() => (darkMode.value ? 'theme--dark' : 'theme--light'));

onMounted(() => {
    const target = resolveThemeTarget();
    darkMode.value = readDarkMode(target);
    themeObserver = new MutationObserver(() => {
        darkMode.value = readDarkMode(resolveThemeTarget());
    });
    themeObserver.observe(target, { attributes: true, attributeFilter: ['class'] });
});

onUnmounted(() => {
    themeObserver?.disconnect();
});

// Reads dark mode from .v-application (Vuetify 2's root) with body as fallback.
function resolveThemeTarget(): Element {
    return document.querySelector('.v-application') ?? document.body;
}

function readDarkMode(el: Element): boolean {
    return el.classList.contains('theme--dark');
}
</script>
